import { logEvent } from '@cortex/faro';
import { extractDateString, normalizeDateArray } from '../date-utils.js';
import type {
  AceMonthlyTrendParams,
  AceMonthlyTrendResponse,
  AuthUrlResponse,
  BudgetFiscalYearStatusResponse,
  BugBreakdownDetailsParams,
  BugBreakdownDetailsResponse,
  BugBreakdownParams,
  BugBreakdownResponse,
  BugDetailParams,
  BugDetailResponse,
  BugEscapeTimingDetailsParams,
  BugEscapeTimingDetailsResponse,
  BugEscapeTimingParams,
  BugEscapeTimingResponse,
  BugSeverityParams,
  BugSeverityResponse,
  CurrentUserResponse,
  DevelopPointDetailParams,
  DevelopPointDetailResponse,
  KpiPointProgressParams,
  KpiPointProgressResponse,
  KpiSummaryParams,
  KpiSummaryResponse,
  KpiTrendParams,
  KpiTrendResponse,
  KpiWeeklyTrendParams,
  KpiWeeklyTrendResponse,
  MonthlyDevelopPointBudgetsResponse,
  QaEvaluationDetailResponse,
  QaEvaluationsParams,
  QaEvaluationsResponse,
  ReleaseDetailParams,
  ReleaseDetailResponse,
} from '../../types/api.js';
import { ApiError, request } from './_request.js';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 認証APIクライアント。getAuthUrl/getCurrentUser/logoutメソッドで認証エンドポイントを呼び出し
 * @graph-connects /auth/login [calls] 認証URL取得API
 * @graph-connects /auth/me [calls] 現在のユーザーセッション取得API
 * @graph-connects /auth/logout [calls] ログアウトAPI
 */
export const authApi = {
  getAuthUrl: () => request<AuthUrlResponse>('/auth/login'),
  getCurrentUser: () =>
    request<CurrentUserResponse>('/auth/me').catch((error: unknown) => {
      // HTTP レスポンスエラー (ApiError) は request() 内で 401=logEvent /
      // 4xx=logEvent / 5xx=logError として既に分類済み。ここで二重に記録しない。
      if (error instanceof ApiError) return null;
      // fetch 自体が reject = ブラウザ側の transient な状況 (TypeError: Failed to
      // fetch / AbortError 等。オフライン・ナビゲーション中の fetch abort・CORS 拒否・
      // preview origin など)。サービス側の障害ではなく、Faro に level=error として
      // 乗せると "Service Error Log Detected" alert を偽発火させる主要源になるため
      // logEvent で記録だけ残す。
      logEvent('auth_check_network_failed', {
        error_name: error instanceof Error ? error.name : 'Unknown',
        error_message: error instanceof Error ? error.message : String(error),
      });
      return null;
    }),
  logout: () => request<undefined>('/auth/logout', { method: 'POST' }),
};

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business KPI APIクライアント。getSummary/getTrend/getWeeklyTrend/getAceMonthlyTrend/getPointProgress/getBugs/getReleases/getDevelopPointsメソッドでKPIエンドポイントを呼び出し
 * @graph-connects /kpi/summary [calls] KPIサマリーAPI
 * @graph-connects /kpi/trend [calls] KPIトレンドAPI
 * @graph-connects /kpi/weekly-trend [calls] 週次pt集計API
 * @graph-connects /kpi/ace-monthly-trend [calls] ACE月次トレンドAPI
 * @graph-connects /kpi/point-progress [calls] pt進捗比較API
 * @graph-connects /kpi/bugs [calls] バグ詳細API
 * @graph-connects /kpi/bug-detection-days [calls] バグ検知日数詳細API
 * @graph-connects /kpi/releases [calls] リリース詳細API
 * @graph-connects /kpi/develop-points [calls] 開発ポイント詳細API
 * @graph-connects /kpi/monthly-budgets [calls] fibo予算 月次開発pt予算API
 * @graph-connects normalizeDateArray [calls] 日付フィールド正規化ユーティリティ呼び出し
 * @graph-connects extractDateString [calls] 日付文字列抽出ユーティリティ呼び出し
 */
export const kpiApi = {
  getSummary: async (params?: KpiSummaryParams): Promise<KpiSummaryResponse> => {
    const query = new URLSearchParams();
    if (params?.date) query.set('date', params.date);
    if (params?.team) query.set('team', params.team);
    const queryString = query.toString();
    const res = await request<KpiSummaryResponse>(
      `/kpi/summary${queryString ? `?${queryString}` : ''}`,
    );
    return {
      ...res,
      date: extractDateString(res.date),
      data: normalizeDateArray(res.data),
    };
  },
  getTrend: async (params?: KpiTrendParams): Promise<KpiTrendResponse> => {
    const query = new URLSearchParams();
    if (params?.team) query.set('team', params.team);
    if (params?.days) query.set('days', String(params.days));
    const queryString = query.toString();
    const res = await request<KpiTrendResponse>(
      `/kpi/trend${queryString ? `?${queryString}` : ''}`,
    );
    return {
      ...res,
      data: normalizeDateArray(res.data),
    };
  },
  getWeeklyTrend: async (params?: KpiWeeklyTrendParams): Promise<KpiWeeklyTrendResponse> => {
    const query = new URLSearchParams();
    if (params?.team) query.set('team', params.team);
    if (params?.weeks !== undefined) query.set('weeks', String(params.weeks));
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    const queryString = query.toString();
    const res = await request<KpiWeeklyTrendResponse>(
      `/kpi/weekly-trend${queryString ? `?${queryString}` : ''}`,
    );
    return {
      ...res,
      data: res.data.map((row) => ({
        ...row,
        weekStart: extractDateString(row.weekStart),
        weekEnd: extractDateString(row.weekEnd),
      })),
    };
  },
  getAceMonthlyTrend: async (params?: AceMonthlyTrendParams): Promise<AceMonthlyTrendResponse> => {
    const query = new URLSearchParams();
    if (params?.team) query.set('team', params.team);
    if (params?.months !== undefined) query.set('months', String(params.months));
    const queryString = query.toString();
    const res = await request<AceMonthlyTrendResponse>(
      `/kpi/ace-monthly-trend${queryString ? `?${queryString}` : ''}`,
    );
    return {
      ...res,
      data: res.data.map((row) => ({
        ...row,
        month: extractDateString(row.month),
      })),
    };
  },
  getPointProgress: async (params?: KpiPointProgressParams): Promise<KpiPointProgressResponse> => {
    const query = new URLSearchParams();
    if (params?.team) query.set('team', params.team);
    if (params?.periodType) query.set('periodType', params.periodType);
    if (params?.period) query.set('period', params.period);
    const queryString = query.toString();
    const res = await request<KpiPointProgressResponse>(
      `/kpi/point-progress${queryString ? `?${queryString}` : ''}`,
    );
    return {
      ...res,
      months: res.months.map((month) => extractDateString(month)),
    };
  },
  getMonthlyBudgets: async (): Promise<MonthlyDevelopPointBudgetsResponse> => {
    return await request<MonthlyDevelopPointBudgetsResponse>('/kpi/monthly-budgets');
  },
  getBudgetFiscalYearStatus: async (params?: {
    now?: string;
    mockFiscalYears?: string;
  }): Promise<BudgetFiscalYearStatusResponse> => {
    const query = new URLSearchParams();
    if (params?.now) query.set('now', params.now);
    if (params?.mockFiscalYears) query.set('mockFiscalYears', params.mockFiscalYears);
    const queryString = query.toString();
    return await request<BudgetFiscalYearStatusResponse>(
      `/kpi/budget-fiscal-year-status${queryString ? `?${queryString}` : ''}`,
    );
  },
  getBugs: async (params?: BugDetailParams): Promise<BugDetailResponse> => {
    const query = new URLSearchParams();
    if (params?.team) query.set('team', params.team);
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    if (params?.limit) query.set('limit', String(params.limit));
    const queryString = query.toString();
    const res = await request<BugDetailResponse>(
      `/kpi/bugs${queryString ? `?${queryString}` : ''}`,
    );
    return {
      ...res,
      data: normalizeDateArray(res.data),
    };
  },
  getBugBreakdown: async (params?: BugBreakdownParams): Promise<BugBreakdownResponse> => {
    const query = new URLSearchParams();
    if (params?.months !== undefined) query.set('months', String(params.months));
    if (params?.unit !== undefined) query.set('unit', params.unit);
    if (params?.team !== undefined) query.set('team', params.team);
    const queryString = query.toString();
    return await request<BugBreakdownResponse>(
      `/kpi/bug-breakdown${queryString ? `?${queryString}` : ''}`,
    );
  },
  getBugBreakdownDetails: async (
    params: BugBreakdownDetailsParams,
  ): Promise<BugBreakdownDetailsResponse> => {
    // month（起票月）か week（起票週の週開始日）のどちらか一方を指定する（API 側で排他必須）
    const query = new URLSearchParams();
    if (params.week !== undefined) {
      query.set('week', params.week);
    } else if (params.month !== undefined) {
      query.set('month', params.month);
    }
    if (params.team !== undefined) query.set('team', params.team);
    return await request<BugBreakdownDetailsResponse>(
      `/kpi/bug-breakdown/details?${query.toString()}`,
    );
  },
  getBugSeverity: async (params?: BugSeverityParams): Promise<BugSeverityResponse> => {
    const query = new URLSearchParams();
    if (params?.months !== undefined) query.set('months', String(params.months));
    if (params?.unit !== undefined) query.set('unit', params.unit);
    if (params?.team !== undefined) query.set('team', params.team);
    const queryString = query.toString();
    return await request<BugSeverityResponse>(
      `/kpi/bug-severity${queryString ? `?${queryString}` : ''}`,
    );
  },
  getBugEscapeTiming: async (params?: BugEscapeTimingParams): Promise<BugEscapeTimingResponse> => {
    const query = new URLSearchParams();
    if (params?.months !== undefined) query.set('months', String(params.months));
    if (params?.team !== undefined) query.set('team', params.team);
    const queryString = query.toString();
    return await request<BugEscapeTimingResponse>(
      `/kpi/bug-escape-timing${queryString ? `?${queryString}` : ''}`,
    );
  },
  getBugEscapeTimingDetails: async (
    params: BugEscapeTimingDetailsParams,
  ): Promise<BugEscapeTimingDetailsResponse> => {
    const query = new URLSearchParams();
    query.set('bin', params.bin);
    if (params.months !== undefined) query.set('months', String(params.months));
    if (params.team !== undefined) query.set('team', params.team);
    return await request<BugEscapeTimingDetailsResponse>(
      `/kpi/bug-escape-timing/details?${query.toString()}`,
    );
  },
  getBugDetectionDays: async (params?: BugDetailParams): Promise<BugDetailResponse> => {
    const query = new URLSearchParams();
    if (params?.team) query.set('team', params.team);
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    if (params?.limit) query.set('limit', String(params.limit));
    const queryString = query.toString();
    const res = await request<BugDetailResponse>(
      `/kpi/bug-detection-days${queryString ? `?${queryString}` : ''}`,
    );
    return {
      ...res,
      data: normalizeDateArray(res.data),
    };
  },
  getReleases: async (params?: ReleaseDetailParams): Promise<ReleaseDetailResponse> => {
    const query = new URLSearchParams();
    if (params?.team) query.set('team', params.team);
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    if (params?.limit) query.set('limit', String(params.limit));
    const queryString = query.toString();
    const res = await request<ReleaseDetailResponse>(
      `/kpi/releases${queryString ? `?${queryString}` : ''}`,
    );
    return {
      ...res,
      data: normalizeDateArray(res.data),
    };
  },
  getDevelopPoints: async (
    params?: DevelopPointDetailParams,
  ): Promise<DevelopPointDetailResponse> => {
    const query = new URLSearchParams();
    if (params?.team) query.set('team', params.team);
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    if (params?.limit) query.set('limit', String(params.limit));
    const queryString = query.toString();
    const res = await request<DevelopPointDetailResponse>(
      `/kpi/develop-points${queryString ? `?${queryString}` : ''}`,
    );
    return {
      ...res,
      data: normalizeDateArray(res.data),
    };
  },
};

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business QA評価APIクライアント。getEvaluations/getEvaluationDetailメソッドでQA評価エンドポイントを呼び出し
 * @graph-connects /qa/evaluations [calls] QA評価一覧API
 * @graph-connects /qa/evaluations/:id [calls] QA評価詳細API
 */
export const qaApi = {
  getEvaluations: async (params?: QaEvaluationsParams): Promise<QaEvaluationsResponse> => {
    const query = new URLSearchParams();
    if (params?.quarter) query.set('quarter', params.quarter);
    if (params?.userEmail) query.set('userEmail', params.userEmail);
    const queryString = query.toString();
    return request<QaEvaluationsResponse>(`/qa/evaluations${queryString ? `?${queryString}` : ''}`);
  },
  getEvaluationDetail: async (
    id: string,
    locale?: 'ja' | 'en',
  ): Promise<QaEvaluationDetailResponse> => {
    const query = locale && locale !== 'ja' ? `?locale=${locale}` : '';
    return request<QaEvaluationDetailResponse>(`/qa/evaluations/${id}${query}`);
  },
};
